/**
 * Rules API Client
 * Manages automation rules for email processing
 */

import { getStoredSessionId } from './auth-client';

const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';
const API_BASE = CF_WORKER_URL ? `${CF_WORKER_URL}/api` : '/api';

export interface RuleCondition {
  field: 'sender' | 'sender_domain' | 'subject' | 'has_attachment';
  operator: 'contains' | 'equals' | 'starts_with' | 'ends_with';
  value: string;
}

export interface RuleActionConfig {
  labelIds?: string[];
  category?: 'do_nothing' | 'delete' | 'archive' | 'unsubscribe';
}

export interface Rule {
  id: string;
  user_id: string;
  name: string;
  description: string;
  is_enabled: number;
  priority: number;
  conditions: RuleCondition[] | null;
  action_type: 'label' | 'category' | 'multi';
  actionConfig: RuleActionConfig;
  times_matched: number;
  last_matched_at: number | null;
  created_at: number;
  updated_at: number;
}

export interface ParsedRule {
  isValid: boolean;
  conditions: RuleCondition[];
  suggestedActions: Array<{
    type: 'apply_label' | 'set_category';
    value: string;
  }>;
  confidence: number;
  clarificationNeeded?: string;
}

export interface CreateRuleInput {
  name: string;
  description: string;
  actionType: 'label' | 'category' | 'multi';
  actionConfig: RuleActionConfig;
  priority?: number;
}

export interface UpdateRuleInput {
  name?: string;
  description?: string;
  actionType?: 'label' | 'category' | 'multi';
  actionConfig?: RuleActionConfig;
  priority?: number;
}

export interface TestRuleResult {
  email: { subject: string; sender: string; hasAttachments?: boolean };
  matches: boolean;
  matchedConditions: RuleCondition[];
}

function getAuthHeaders(): HeadersInit {
  const sessionId = getStoredSessionId();
  return sessionId ? { 'X-Session-Id': sessionId } : {};
}

/**
 * Fetch all rules for the current user
 */
export async function fetchRules(): Promise<Rule[]> {
  const response = await fetch(`${API_BASE}/rules`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch rules');
  }

  const data = await response.json();
  return data.data.rules;
}

/**
 * Create a new rule
 */
export async function createRule(input: CreateRuleInput): Promise<{ rule: Rule; parsedRule: ParsedRule }> {
  const response = await fetch(`${API_BASE}/rules`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to create rule');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Get a specific rule
 */
export async function getRule(id: string): Promise<Rule> {
  const response = await fetch(`${API_BASE}/rules/${id}`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch rule');
  }

  const data = await response.json();
  return data.data.rule;
}

/**
 * Update a rule
 */
export async function updateRule(id: string, input: UpdateRuleInput): Promise<Rule> {
  const response = await fetch(`${API_BASE}/rules/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to update rule');
  }

  const data = await response.json();
  return data.data.rule;
}

/**
 * Delete a rule
 */
export async function deleteRule(id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/rules/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to delete rule');
  }
}

/**
 * Toggle a rule's enabled status
 */
export async function toggleRule(id: string): Promise<boolean> {
  const response = await fetch(`${API_BASE}/rules/${id}/toggle`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to toggle rule');
  }

  const data = await response.json();
  return data.data.isEnabled;
}

/**
 * Test a rule against sample emails
 */
export async function testRule(
  description: string,
  sampleEmails: Array<{ subject: string; sender: string; hasAttachments?: boolean }>
): Promise<{ parsedRule: ParsedRule; results: TestRuleResult[] }> {
  const response = await fetch(`${API_BASE}/rules/test`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ description, sampleEmails }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to test rule');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Get rule statistics
 */
export async function getRuleStats(id: string): Promise<{ timesMatched: number; lastMatchedAt: number | null }> {
  const response = await fetch(`${API_BASE}/rules/${id}/stats`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch rule stats');
  }

  const data = await response.json();
  return data.data;
}

export interface ApplyRulesResult {
  message: string;
  emailsProcessed: number;
  emailsMatched: number;
  labelsApplied: number;
  rulesApplied: number;
}

/**
 * Apply all enabled rules to existing emails
 * This retroactively evaluates rules against analyzed emails
 */
export async function applyRulesToExistingEmails(): Promise<ApplyRulesResult> {
  const response = await fetch(`${API_BASE}/rules/apply`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to apply rules');
  }

  const data = await response.json();
  return data.data;
}
